/**
 * Calculates the absolute difference in days between today and a target date.
 * Uses UTC midnight for both dates to avoid timezone-related discrepancies.
 *
 * @param targetDate - The date to calculate the difference from today
 * @returns Object containing the absolute day difference (rounded up) and the current timestamp
 *
 * @remarks
 * - The calculation is always positive (uses Math.abs)
 * - Days are rounded up using Math.ceil, so partial days count as full days
 * - Both dates are normalized to UTC midnight before calculation
 *
 * @example
 * // Calculate days from today to a future date
 * const result = getDaysFromToday(new Date('2025-12-31'))
 * console.log(result.daysDifference) // e.g., 19
 *
 * // Works with past dates too (always returns positive)
 * const past = getDaysFromToday(new Date('2025-01-01'))
 * console.log(past.daysDifference) // e.g., 345
 */
export function getDaysFromToday(targetDate: Date) {
  const currentDate = new Date()

  // Normalize both dates to UTC midnight to avoid timezone issues
  const targetUtcMidnight = Date.UTC(targetDate.getUTCFullYear(), targetDate.getUTCMonth(), targetDate.getUTCDate())
  const currentUtcMidnight = Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate())

  // Calculate absolute difference in milliseconds
  const timeDifferenceMs = Math.abs(targetUtcMidnight - currentUtcMidnight)

  // Convert to days and round up
  const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24
  const daysDifference = Math.ceil(timeDifferenceMs / MILLISECONDS_PER_DAY)

  return {
    daysDifference,
    currentTimestamp: currentDate,
  }
}
